"use client";

/**
 * MapField
 * ------------------------------------------------------------------
 * Deterministic abstract map graphic in five variants:
 *   - "contour"  : isolines from a seeded noise field (marching squares)
 *   - "voronoi"  : Thiessen polygons from seeded points
 *   - "hex"      : H3-style pointy-top hexagonal grid
 *   - "delaunay" : Delaunay triangulation of the same point set as voronoi
 *   - "parcels"  : recursive binary space partition (cadastral feel)
 *
 * Optional animation: a "window" of emphasis cycles through the value
 * range, wrapping seamlessly so different elements take their turn as
 * the index colour. Set `animate` and tune `duration`.
 *
 * Same seed -> byte-identical generation. Without `animate`, the output
 * is deterministic and frame-stable. With `animate`, only the styling
 * changes per frame; the underlying geometry stays constant.
 *
 * Note: this file is "use client" because `animate` needs RAF. If you
 * want zero-JS static rendering and don't need animation, the generation
 * functions and rendering helpers below are pure and can be lifted into
 * a server component.
 *
 *   import { MapField } from "mangomap-ui";
 *   <MapField seed="home-hero" />
 *   <MapField seed="pricing" variant="hex" animate duration={12} />
 */

import { useCallback, useEffect, useMemo, useRef } from "react";

import {
	chateauGreen,
	neonBlue,
	shakespeare,
	stormGrey,
	tomatoOrange,
} from "../../styles/theme/colors";

export interface MapPalette {
	line: string;
	index: string;
}

export type MapFieldVariant =
	| "contour"
	| "voronoi"
	| "hex"
	| "delaunay"
	| "parcels";

export interface MapFieldProps {
	seed: string;
	variant?: MapFieldVariant;
	/** Explicit pixel width. When neither `width` nor `height` is given, the
	 *  graphic fills its parent's width at the `aspect` ratio. A missing
	 *  dimension is derived from the other via `aspect`. */
	width?: number;
	height?: number;
	/** Aspect ratio (width ÷ height) used whenever the size is not pinned by an
	 *  explicit `width`/`height`. Accepts a number (e.g. `1.5`) or a string
	 *  (`"8/3"`, `"16:9"`). Defaults to 4:3. */
	aspect?: number | string;
	palette?: MapPalette;
	strokeWidth?: number;
	octaves?: number;

	/** Contour-only. */
	levels?: number;
	cols?: number;
	rows?: number;
	indexEvery?: number;

	/** Voronoi and Delaunay. */
	cellCount?: number;
	/** Voronoi, Delaunay, Hex, Parcels. Top fraction by elevation emphasised
	 *  (and, when animating, the width of the moving emphasis window). */
	indexFraction?: number;
	showSeedDots?: boolean;

	hexSize?: number;
	hexActiveThreshold?: number;

	parcelMaxDepth?: number;
	parcelMinSize?: number;

	/** Cycle a sliding emphasis window through the value range. */
	animate?: boolean;
	/** Seconds for one full cycle. */
	duration?: number;

	/** Clamp the canvas backing-store resolution. The raster is sized at
	 *  min(devicePixelRatio, maxDpr); lower values trade sharpness for memory.
	 *  Useful for large, low-opacity decorative fields. Default: uncapped. */
	maxDpr?: number;

	className?: string;
	style?: React.CSSProperties;
	ariaLabel?: string;
}

/**
 * Stroke palettes derived from the shared MangoMap colour tokens
 * (`src/styles/theme/colors`) rather than one-off hex values, so the graphic
 * stays in step with the brand palette. The component paints no background —
 * only the strokes are coloured — so each palette is just
 * `{ line: scale[400|500], index: scale[800|900] }`. The first five suit light
 * backgrounds; `onDark` uses light strokes for dark backgrounds.
 *
 * Concrete hex strings (not `var(--mui-palette-*)`) are required here: the
 * animated styling interpolates between `line` and `index` with `lerpHex`,
 * which parses literal `#rrggbb` values.
 */
export const mapPalettes = {
	bathymetric: { line: shakespeare[500], index: shakespeare[900] },
	topoPaper: { line: tomatoOrange[400], index: tomatoOrange[800] },
	indigoInk: { line: neonBlue[400], index: neonBlue[900] },
	forest: { line: chateauGreen[400], index: chateauGreen[800] },
	slate: { line: stormGrey[400], index: stormGrey[700] },
	onDark: { line: stormGrey[300], index: stormGrey[100] },
} satisfies Record<string, MapPalette>;

/* =================================================================
   PRNG + value noise (shared)
   ================================================================= */
function hashSeed(str: string): number {
	let h = 1779033703 ^ str.length;
	for (let i = 0; i < str.length; i++) {
		h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
		h = (h << 13) | (h >>> 19);
	}
	h = Math.imul(h ^ (h >>> 16), 2246822507);
	h = Math.imul(h ^ (h >>> 13), 3266489909);
	return (h ^ (h >>> 16)) >>> 0;
}
function mulberry32(a: number): () => number {
	return function () {
		a |= 0;
		a = (a + 0x6d2b79f5) | 0;
		let t = Math.imul(a ^ (a >>> 15), 1 | a);
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}
type Sampler = (u: number, v: number) => number;
function makeValueNoise(rand: () => number, gridSize: number): Sampler {
	const n = gridSize + 1;
	const grid = new Float32Array(n * n);
	for (let i = 0; i < n * n; i++) grid[i] = rand();
	const fade = (t: number) => t * t * (3 - 2 * t);
	return (u, v) => {
		const gx = u * gridSize;
		const gy = v * gridSize;
		let x0 = Math.floor(gx);
		let y0 = Math.floor(gy);
		if (x0 >= gridSize) x0 = gridSize - 1;
		if (y0 >= gridSize) y0 = gridSize - 1;
		const tx = fade(gx - x0);
		const ty = fade(gy - y0);
		const a = grid[y0 * n + x0]!;
		const b = grid[y0 * n + x0 + 1]!;
		const c = grid[(y0 + 1) * n + x0]!;
		const d = grid[(y0 + 1) * n + x0 + 1]!;
		const top = a + (b - a) * tx;
		const bot = c + (d - c) * tx;
		return top + (bot - top) * ty;
	};
}
function makeFractalNoise(rand: () => number, octaves: number): Sampler {
	const layers: { noise: Sampler; amp: number }[] = [];
	let g = 2;
	for (let o = 0; o < octaves; o++) {
		layers.push({ noise: makeValueNoise(rand, g), amp: 1 / (o + 1) });
		g *= 2;
	}
	const ampSum = layers.reduce((s, l) => s + l.amp, 0);
	return (u, v) => {
		let val = 0;
		for (const { noise, amp } of layers) val += noise(u, v) * amp;
		return val / ampSum;
	};
}
function normalise<T extends { v: number }>(items: T[]): void {
	let min = Infinity;
	let max = -Infinity;
	for (const it of items) {
		if (it.v < min) min = it.v;
		if (it.v > max) max = it.v;
	}
	const range = max - min || 1;
	for (const it of items) it.v = (it.v - min) / range;
}

/* =================================================================
   Animation helpers
   ================================================================= */
function lerpHex(a: string, b: string, t: number): string {
	const tt = Math.max(0, Math.min(1, t));
	const pa = [
		parseInt(a.slice(1, 3), 16),
		parseInt(a.slice(3, 5), 16),
		parseInt(a.slice(5, 7), 16),
	];
	const pb = [
		parseInt(b.slice(1, 3), 16),
		parseInt(b.slice(3, 5), 16),
		parseInt(b.slice(5, 7), 16),
	];
	return (
		"#" +
		pa
			.map((v, i) =>
				Math.round(v + (pb[i]! - v) * tt)
					.toString(16)
					.padStart(2, "0"),
			)
			.join("")
	);
}
/** Smoothstep intensity for value `v` against a sliding window of width
 *  `width` centred at `phase`, with circular wrap so phase=0 and phase=1
 *  are the same point. Returns 0 outside the window, 1 at the centre. */
function windowIntensity(v: number, phase: number, width: number): number {
	if (width <= 0) return 0;
	const half = width / 2;
	const lin = Math.abs(v - phase);
	const dist = Math.min(lin, 1 - lin);
	if (dist >= half) return 0;
	const t = 1 - dist / half;
	return t * t * (3 - 2 * t);
}

/* =================================================================
   CONTOUR
   ================================================================= */
function makeField(
	seed: string,
	cols: number,
	rows: number,
	octaves: number,
): Float32Array[] {
	const rand = mulberry32(hashSeed(seed));
	const sample = makeFractalNoise(rand, octaves);
	let min = Infinity;
	let max = -Infinity;
	const field: Float32Array[] = [];
	for (let r = 0; r <= rows; r++) {
		const row = new Float32Array(cols + 1);
		for (let c = 0; c <= cols; c++) {
			const v = sample(c / cols, r / rows);
			row[c] = v;
			if (v < min) min = v;
			if (v > max) max = v;
		}
		field.push(row);
	}
	const range = max - min || 1;
	for (let r = 0; r <= rows; r++)
		for (let c = 0; c <= cols; c++)
			field[r]![c] = (field[r]![c]! - min) / range;
	return field;
}
function contourPaths(
	field: Float32Array[],
	cols: number,
	rows: number,
	levelValues: number[],
	w: number,
	h: number,
): string[] {
	const cw = w / cols;
	const ch = h / rows;
	const interp = (
		lv: number,
		ax: number,
		ay: number,
		av: number,
		bx: number,
		by: number,
		bv: number,
	): [number, number] => {
		const t = (lv - av) / (bv - av);
		return [ax + (bx - ax) * t, ay + (by - ay) * t];
	};
	return levelValues.map((level) => {
		let d = "";
		for (let r = 0; r < rows; r++) {
			for (let c = 0; c < cols; c++) {
				const x = c * cw;
				const y = r * ch;
				const tl = field[r]![c]!;
				const tr = field[r]![c + 1]!;
				const br = field[r + 1]![c + 1]!;
				const bl = field[r + 1]![c]!;
				let idx = 0;
				if (tl > level) idx |= 8;
				if (tr > level) idx |= 4;
				if (br > level) idx |= 2;
				if (bl > level) idx |= 1;
				if (idx === 0 || idx === 15) continue;
				const top = () => interp(level, x, y, tl, x + cw, y, tr);
				const right = () => interp(level, x + cw, y, tr, x + cw, y + ch, br);
				const bottom = () => interp(level, x + cw, y + ch, br, x, y + ch, bl);
				const left = () => interp(level, x, y, tl, x, y + ch, bl);
				const seg = (p: [number, number], q: [number, number]) => {
					d += `M${p[0].toFixed(2)} ${p[1].toFixed(2)}L${q[0].toFixed(2)} ${q[1].toFixed(2)}`;
				};
				switch (idx) {
					case 1:
					case 14:
						seg(left(), bottom());
						break;
					case 2:
					case 13:
						seg(bottom(), right());
						break;
					case 3:
					case 12:
						seg(left(), right());
						break;
					case 4:
					case 11:
						seg(top(), right());
						break;
					case 6:
					case 9:
						seg(top(), bottom());
						break;
					case 7:
					case 8:
						seg(left(), top());
						break;
					case 5:
						seg(left(), bottom());
						seg(top(), right());
						break;
					case 10:
						seg(left(), top());
						seg(bottom(), right());
						break;
				}
			}
		}
		return d;
	});
}

/* =================================================================
   Point distribution + voronoi
   ================================================================= */
function mitchellsPoints(
	rand: () => number,
	count: number,
	x0: number,
	y0: number,
	x1: number,
	y1: number,
): { x: number; y: number }[] {
	const pts: { x: number; y: number }[] = [];
	const W = x1 - x0;
	const H = y1 - y0;
	for (let i = 0; i < count; i++) {
		let best: { x: number; y: number } | null = null;
		let bestDist = -1;
		for (let k = 0; k < 14; k++) {
			const sx = x0 + rand() * W;
			const sy = y0 + rand() * H;
			let minD = Infinity;
			for (const p of pts) {
				const d = (p.x - sx) ** 2 + (p.y - sy) ** 2;
				if (d < minD) minD = d;
			}
			if (minD > bestDist) {
				bestDist = minD;
				best = { x: sx, y: sy };
			}
		}
		pts.push(best!);
	}
	return pts;
}

interface Cell {
	polygon: [number, number][];
	v: number;
	seed: { x: number; y: number };
}
function clipByHalfPlane(
	polygon: [number, number][],
	a: number,
	b: number,
	c: number,
): [number, number][] {
	const result: [number, number][] = [];
	const n = polygon.length;
	if (n === 0) return result;
	for (let i = 0; i < n; i++) {
		const cur = polygon[i]!;
		const next = polygon[(i + 1) % n]!;
		const curVal = a * cur[0] + b * cur[1] + c;
		const nextVal = a * next[0] + b * next[1] + c;
		const curIn = curVal <= 0;
		const nextIn = nextVal <= 0;
		if (curIn) result.push(cur);
		if (curIn !== nextIn) {
			const t = curVal / (curVal - nextVal);
			result.push([
				cur[0] + t * (next[0] - cur[0]),
				cur[1] + t * (next[1] - cur[1]),
			]);
		}
	}
	return result;
}
function makeVoronoi(
	seed: string,
	cellCount: number,
	octaves: number,
	x0: number,
	y0: number,
	x1: number,
	y1: number,
): Cell[] {
	const rand = mulberry32(hashSeed(seed));
	const seeds = mitchellsPoints(rand, cellCount, x0, y0, x1, y1);
	const sample = makeFractalNoise(rand, octaves);
	const enriched = seeds.map((s) => ({
		...s,
		v: sample((s.x - x0) / (x1 - x0), (s.y - y0) / (y1 - y0)),
	}));
	normalise(enriched);
	const cells: Cell[] = [];
	for (let i = 0; i < enriched.length; i++) {
		let poly: [number, number][] = [
			[x0, y0],
			[x1, y0],
			[x1, y1],
			[x0, y1],
		];
		const ei = enriched[i]!;
		for (let j = 0; j < enriched.length; j++) {
			if (i === j) continue;
			const ej = enriched[j]!;
			const a = 2 * (ej.x - ei.x);
			const b = 2 * (ej.y - ei.y);
			const c = ei.x * ei.x + ei.y * ei.y - ej.x * ej.x - ej.y * ej.y;
			poly = clipByHalfPlane(poly, a, b, c);
			if (poly.length === 0) break;
		}
		cells.push({
			polygon: poly,
			v: ei.v,
			seed: { x: ei.x, y: ei.y },
		});
	}
	return cells;
}

/* =================================================================
   DELAUNAY (Bowyer-Watson)
   ================================================================= */
interface DPoint {
	x: number;
	y: number;
	v?: number;
	sup?: boolean;
}
interface Tri {
	a: DPoint;
	b: DPoint;
	c: DPoint;
}

function isCCW(a: DPoint, b: DPoint, c: DPoint): boolean {
	return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x) > 0;
}
function makeTri(a: DPoint, b: DPoint, c: DPoint): Tri {
	return isCCW(a, b, c) ? { a, b, c } : { a, b: c, c: b };
}
function inCircumcircle(p: DPoint, t: Tri): boolean {
	const ax = t.a.x - p.x;
	const ay = t.a.y - p.y;
	const bx = t.b.x - p.x;
	const by = t.b.y - p.y;
	const cx = t.c.x - p.x;
	const cy = t.c.y - p.y;
	const det =
		(ax * ax + ay * ay) * (bx * cy - by * cx) -
		(bx * bx + by * by) * (ax * cy - ay * cx) +
		(cx * cx + cy * cy) * (ax * by - ay * bx);
	return det > 0;
}
function edgeKey(a: DPoint, b: DPoint): string {
	const ka = `${a.x},${a.y}`;
	const kb = `${b.x},${b.y}`;
	return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
}
function delaunayTriangles(points: DPoint[]): Tri[] {
	if (points.length < 3) return [];
	let minX = Infinity,
		maxX = -Infinity,
		minY = Infinity,
		maxY = -Infinity;
	for (const p of points) {
		if (p.x < minX) minX = p.x;
		if (p.x > maxX) maxX = p.x;
		if (p.y < minY) minY = p.y;
		if (p.y > maxY) maxY = p.y;
	}
	const dMax = Math.max(maxX - minX, maxY - minY) * 20;
	const midX = (minX + maxX) / 2;
	const midY = (minY + maxY) / 2;
	const s1: DPoint = { x: midX - dMax, y: midY - dMax, sup: true };
	const s2: DPoint = { x: midX + dMax, y: midY - dMax, sup: true };
	const s3: DPoint = { x: midX, y: midY + dMax, sup: true };
	let tris: Tri[] = [makeTri(s1, s2, s3)];
	for (const p of points) {
		const bad: Tri[] = [];
		for (const t of tris) if (inCircumcircle(p, t)) bad.push(t);
		const ec = new Map<string, number>();
		for (const t of bad) {
			for (const [a, b] of [
				[t.a, t.b],
				[t.b, t.c],
				[t.c, t.a],
			] as [DPoint, DPoint][]) {
				const k = edgeKey(a, b);
				ec.set(k, (ec.get(k) ?? 0) + 1);
			}
		}
		const boundary: [DPoint, DPoint][] = [];
		for (const t of bad) {
			for (const [a, b] of [
				[t.a, t.b],
				[t.b, t.c],
				[t.c, t.a],
			] as [DPoint, DPoint][]) {
				if (ec.get(edgeKey(a, b)) === 1) boundary.push([a, b]);
			}
		}
		const badSet = new Set(bad);
		tris = tris.filter((t) => !badSet.has(t));
		for (const [a, b] of boundary) tris.push(makeTri(a, b, p));
	}
	return tris.filter((t) => !t.a.sup && !t.b.sup && !t.c.sup);
}
function makeDelaunay(
	seed: string,
	count: number,
	octaves: number,
	x0: number,
	y0: number,
	x1: number,
	y1: number,
): Tri[] {
	const rand = mulberry32(hashSeed(seed));
	const pts = mitchellsPoints(rand, count, x0, y0, x1, y1);
	const sample = makeFractalNoise(rand, octaves);
	const verts: DPoint[] = pts.map((p) => ({
		x: p.x,
		y: p.y,
		v: sample((p.x - x0) / (x1 - x0), (p.y - y0) / (y1 - y0)),
	}));
	normalise(verts as { v: number }[]);
	return delaunayTriangles(verts);
}

/* =================================================================
   HEX
   ================================================================= */
interface Hex {
	cx: number;
	cy: number;
	v: number;
}
function makeHexGrid(
	seed: string,
	size: number,
	octaves: number,
	x0: number,
	y0: number,
	x1: number,
	y1: number,
): Hex[] {
	const rand = mulberry32(hashSeed(seed));
	const sample = makeFractalNoise(rand, octaves);
	const W = x1 - x0;
	const H = y1 - y0;
	const hexW = Math.sqrt(3) * size;
	const ySpacing = 1.5 * size;
	const cols = Math.ceil(W / hexW) + 2;
	const rows = Math.ceil(H / ySpacing) + 2;
	const offsetX = x0 + (W - (cols - 1) * hexW) / 2;
	const offsetY = y0 + (H - (rows - 1) * ySpacing) / 2;
	const hexes: Hex[] = [];
	for (let r = 0; r < rows; r++) {
		for (let c = 0; c < cols; c++) {
			const cx = offsetX + c * hexW + (r % 2 === 1 ? hexW / 2 : 0);
			const cy = offsetY + r * ySpacing;
			const u = Math.max(0, Math.min(1, (cx - x0) / W));
			const v = Math.max(0, Math.min(1, (cy - y0) / H));
			hexes.push({ cx, cy, v: sample(u, v) });
		}
	}
	normalise(hexes);
	return hexes;
}
function hexPolygonCoords(
	cx: number,
	cy: number,
	s: number,
): [number, number][] {
	const half = s / 2;
	const h = (Math.sqrt(3) / 2) * s;
	return [
		[cx, cy - s],
		[cx + h, cy - half],
		[cx + h, cy + half],
		[cx, cy + s],
		[cx - h, cy + half],
		[cx - h, cy - half],
	];
}

/* =================================================================
   PARCELS
   ================================================================= */
interface Parcel {
	x0: number;
	y0: number;
	x1: number;
	y1: number;
	v: number;
}
function makeParcels(
	seed: string,
	maxDepth: number,
	minSize: number,
	octaves: number,
	x0: number,
	y0: number,
	x1: number,
	y1: number,
): Parcel[] {
	const rand = mulberry32(hashSeed(seed));
	const sample = makeFractalNoise(rand, octaves);
	const W = x1 - x0;
	const H = y1 - y0;
	const sN = (cx: number, cy: number) => sample((cx - x0) / W, (cy - y0) / H);
	const parcels: Parcel[] = [];
	function recurse(
		ax: number,
		ay: number,
		bx: number,
		by: number,
		depth: number,
	) {
		const w = bx - ax;
		const h = by - ay;
		const cx = (ax + bx) / 2;
		const cy = (ay + by) / 2;
		const noise = sN(cx, cy);
		if (depth >= maxDepth || (w < minSize && h < minSize)) {
			parcels.push({ x0: ax, y0: ay, x1: bx, y1: by, v: noise });
			return;
		}
		if (depth >= 3) {
			const keepProb = 0.35 + noise * 0.6;
			if (rand() > keepProb) {
				parcels.push({ x0: ax, y0: ay, x1: bx, y1: by, v: noise });
				return;
			}
		}
		const aspect = w / h;
		let splitVertical: boolean;
		if (aspect > 1.5) splitVertical = true;
		else if (aspect < 0.67) splitVertical = false;
		else splitVertical = rand() < 0.5;
		const t = 0.32 + rand() * 0.36;
		if (splitVertical) {
			const sx = ax + t * w;
			recurse(ax, ay, sx, by, depth + 1);
			recurse(sx, ay, bx, by, depth + 1);
		} else {
			const sy = ay + t * h;
			recurse(ax, ay, bx, sy, depth + 1);
			recurse(ax, sy, bx, by, depth + 1);
		}
	}
	recurse(x0, y0, x1, y1, 0);
	normalise(parcels);
	return parcels;
}

/* =================================================================
   Style helper: maps an element value to stroke colour, width, opacity.
   Static path uses the existing threshold logic. Animated path uses
   the sliding window with smoothstep intensity.
   ================================================================= */
function styleFor(
	v: number,
	baseOp: number,
	palette: MapPalette,
	strokeWidth: number,
	indexFraction: number,
	animate: boolean,
	phase: number,
): { stroke: string; strokeWidth: number; strokeOpacity: number } {
	if (animate) {
		const i = windowIntensity(v, phase, indexFraction);
		return {
			stroke: i > 0 ? lerpHex(palette.line, palette.index, i) : palette.line,
			strokeWidth: strokeWidth * (1 + i * 0.6),
			strokeOpacity: baseOp + i * (0.9 - baseOp),
		};
	}
	const isIndex = v > 1 - indexFraction;
	return {
		stroke: isIndex ? palette.index : palette.line,
		strokeWidth: isIndex ? strokeWidth * 1.6 : strokeWidth,
		strokeOpacity: isIndex ? 0.9 : baseOp,
	};
}

/** Parse an aspect ratio (width ÷ height) from a number or a `"w/h"` / `"w:h"`
 *  string. Falls back to 4:3 for anything unparseable. */
function resolveAspect(aspect: number | string): number {
	if (typeof aspect === "number") return aspect > 0 ? aspect : 4 / 3;
	const parts = aspect.split(/[/:]/);
	if (parts.length === 2) {
		const w = Number(parts[0]);
		const h = Number(parts[1]);
		if (w > 0 && h > 0) return w / h;
	}
	const single = Number(aspect);
	return single > 0 ? single : 4 / 3;
}

/* =================================================================
   COMPONENT
   ================================================================= */
export function MapField({
	seed,
	variant = "contour",
	width,
	height,
	aspect = 4 / 3,
	palette = mapPalettes.bathymetric,
	strokeWidth = 1,
	octaves = 3,
	levels = 11,
	cols = 72,
	rows = 48,
	indexEvery = 5,
	cellCount = 60,
	indexFraction = 0.22,
	showSeedDots = false,
	hexSize = 22,
	hexActiveThreshold = 0,
	parcelMaxDepth = 7,
	parcelMinSize = 30,
	animate = false,
	duration = 12,
	maxDpr,
	className,
	style,
	ariaLabel = "Abstract map graphic",
}: MapFieldProps) {
	// Resolve the coordinate space used for generation + the SVG viewBox. With
	// no explicit size the graphic is responsive (fills parent width at the
	// `aspect` ratio); any explicit dimension renders it at a fixed pixel size,
	// deriving the missing dimension from `aspect`.
	const ar = resolveAspect(aspect);
	const responsive = width == null && height == null;
	const vbWidth = width ?? (height != null ? height * ar : 1200);
	const vbHeight = height ?? (width != null ? width / ar : 1200 / ar);

	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	// Latest canvas CSS box + devicePixelRatio, kept in a ref so the rAF/draw
	// path never triggers a React re-render.
	const sizeRef = useRef({ cssH: 0, cssW: 0, dpr: 1 });
	const phaseRef = useRef(0);

	// generated geometry (re-computed only when generation params change)
	const data = useMemo(() => {
		if (variant === "contour") {
			const field = makeField(seed, cols, rows, octaves);
			const levelValues = Array.from(
				{ length: levels },
				(_, i) => (i + 1) / (levels + 1),
			);
			return {
				kind: "contour" as const,
				paths: contourPaths(field, cols, rows, levelValues, vbWidth, vbHeight),
			};
		}
		if (variant === "voronoi") {
			return {
				kind: "voronoi" as const,
				cells: makeVoronoi(seed, cellCount, octaves, 0, 0, vbWidth, vbHeight),
			};
		}
		if (variant === "delaunay") {
			return {
				kind: "delaunay" as const,
				tris: makeDelaunay(seed, cellCount, octaves, 0, 0, vbWidth, vbHeight),
			};
		}
		if (variant === "hex") {
			return {
				kind: "hex" as const,
				hexes: makeHexGrid(seed, hexSize, octaves, 0, 0, vbWidth, vbHeight),
			};
		}
		return {
			kind: "parcels" as const,
			parcels: makeParcels(
				seed,
				parcelMaxDepth,
				parcelMinSize,
				octaves,
				0,
				0,
				vbWidth,
				vbHeight,
			),
		};
	}, [
		variant,
		seed,
		vbWidth,
		vbHeight,
		octaves,
		levels,
		cols,
		rows,
		cellCount,
		hexSize,
		parcelMaxDepth,
		parcelMinSize,
	]);

	// Parse the contour path strings into Path2D objects once per geometry change
	// so the rAF draw loop can reuse them instead of reparsing every frame. Guarded
	// for SSR, where Path2D is undefined.
	const contourPath2Ds = useMemo(() => {
		if (data.kind !== "contour" || typeof Path2D === "undefined") return null;
		return data.paths.map((d) => new Path2D(d));
	}, [data]);

	// Paint the current geometry to the canvas at the given animation phase.
	// Pure imperative drawing: no React elements, no per-frame allocation beyond
	// the small number of `lerpHex` calls for elements inside the emphasis window.
	const draw = useCallback(
		(phase: number) => {
			const canvas = canvasRef.current;
			const ctx = canvas?.getContext("2d");
			if (!canvas || !ctx) return;
			const { cssW, cssH, dpr } = sizeRef.current;
			if (cssW === 0 || cssH === 0) return;

			// Cover-fit transform, replicating SVG preserveAspectRatio="xMidYMid
			// slice": scale to fill, centre, and let the canvas clip the overflow.
			const scale = Math.max(cssW / vbWidth, cssH / vbHeight);
			const tx = (cssW - vbWidth * scale) / 2;
			const ty = (cssH - vbHeight * scale) / 2;
			ctx.setTransform(1, 0, 0, 1, 0, 0);
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			ctx.setTransform(dpr * scale, 0, 0, dpr * scale, dpr * tx, dpr * ty);

			if (data.kind === "contour") {
				ctx.lineCap = "round";
				ctx.lineJoin = "round";
				data.paths.forEach((d, i) => {
					const v = (i + 1) / (levels + 1);
					const baseOp = 0.3 + v * 0.45;
					const isIndexStatic = (i + 1) % indexEvery === 0;
					const s = animate
						? styleFor(
								v,
								baseOp,
								palette,
								strokeWidth,
								indexFraction,
								true,
								phase,
							)
						: {
								stroke: isIndexStatic ? palette.index : palette.line,
								strokeWidth: isIndexStatic ? strokeWidth * 1.6 : strokeWidth,
								strokeOpacity: isIndexStatic ? 0.9 : baseOp,
							};
					ctx.strokeStyle = s.stroke;
					ctx.lineWidth = s.strokeWidth;
					ctx.globalAlpha = s.strokeOpacity;
					ctx.stroke(contourPath2Ds?.[i] ?? new Path2D(d));
				});
			} else if (data.kind === "voronoi") {
				ctx.lineCap = "butt";
				ctx.lineJoin = "round";
				for (const cell of data.cells) {
					if (cell.polygon.length < 3) continue;
					const baseOp = 0.45 + cell.v * 0.35;
					const s = styleFor(
						cell.v,
						baseOp,
						palette,
						strokeWidth,
						indexFraction,
						animate,
						phase,
					);
					ctx.strokeStyle = s.stroke;
					ctx.lineWidth = s.strokeWidth;
					ctx.globalAlpha = s.strokeOpacity;
					ctx.beginPath();
					cell.polygon.forEach((p, i) =>
						i === 0 ? ctx.moveTo(p[0], p[1]) : ctx.lineTo(p[0], p[1]),
					);
					ctx.closePath();
					ctx.stroke();
				}
				if (showSeedDots) {
					for (const cell of data.cells) {
						const showAsIndex = animate
							? windowIntensity(cell.v, phase, indexFraction) > 0.5
							: cell.v > 1 - indexFraction;
						ctx.fillStyle = showAsIndex ? palette.index : palette.line;
						ctx.globalAlpha = 0.85;
						ctx.beginPath();
						ctx.arc(cell.seed.x, cell.seed.y, showAsIndex ? 1.6 : 0.9, 0, TAU);
						ctx.fill();
					}
				}
			} else if (data.kind === "delaunay") {
				ctx.lineCap = "butt";
				ctx.lineJoin = "round";
				for (const t of data.tris) {
					const v = ((t.a.v ?? 0) + (t.b.v ?? 0) + (t.c.v ?? 0)) / 3;
					const baseOp = 0.45 + v * 0.35;
					const s = styleFor(
						v,
						baseOp,
						palette,
						strokeWidth,
						indexFraction,
						animate,
						phase,
					);
					ctx.strokeStyle = s.stroke;
					ctx.lineWidth = s.strokeWidth;
					ctx.globalAlpha = s.strokeOpacity;
					ctx.beginPath();
					ctx.moveTo(t.a.x, t.a.y);
					ctx.lineTo(t.b.x, t.b.y);
					ctx.lineTo(t.c.x, t.c.y);
					ctx.closePath();
					ctx.stroke();
				}
			} else if (data.kind === "hex") {
				ctx.lineCap = "butt";
				ctx.lineJoin = "round";
				for (const h of data.hexes) {
					if (h.v < hexActiveThreshold) continue;
					const baseOp = 0.4 + h.v * 0.4;
					const s = styleFor(
						h.v,
						baseOp,
						palette,
						strokeWidth,
						indexFraction,
						animate,
						phase,
					);
					ctx.strokeStyle = s.stroke;
					ctx.lineWidth = s.strokeWidth;
					ctx.globalAlpha = s.strokeOpacity;
					ctx.beginPath();
					hexPolygonCoords(h.cx, h.cy, hexSize).forEach((p, i) =>
						i === 0 ? ctx.moveTo(p[0], p[1]) : ctx.lineTo(p[0], p[1]),
					);
					ctx.closePath();
					ctx.stroke();
				}
			} else {
				ctx.lineCap = "butt";
				ctx.lineJoin = "miter";
				for (const p of data.parcels) {
					const baseOp = 0.4 + p.v * 0.4;
					const s = styleFor(
						p.v,
						baseOp,
						palette,
						strokeWidth,
						indexFraction,
						animate,
						phase,
					);
					ctx.strokeStyle = s.stroke;
					ctx.lineWidth = s.strokeWidth;
					ctx.globalAlpha = s.strokeOpacity;
					ctx.strokeRect(p.x0, p.y0, p.x1 - p.x0, p.y1 - p.y0);
				}
			}
			ctx.globalAlpha = 1;
		},
		[
			data,
			contourPath2Ds,
			vbWidth,
			vbHeight,
			palette,
			strokeWidth,
			indexFraction,
			animate,
			showSeedDots,
			hexActiveThreshold,
			hexSize,
			levels,
			indexEvery,
		],
	);

	// Track the canvas's rendered size + DPR, size the backing store crisply, and
	// repaint. Covers the static case and re-runs when `draw` (i.e. geometry or
	// style inputs) changes.
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const measure = () => {
			const cssW = canvas.clientWidth;
			const cssH = canvas.clientHeight;
			const dpr = Math.min(window.devicePixelRatio || 1, maxDpr ?? Infinity);
			sizeRef.current = { cssH, cssW, dpr };
			canvas.width = Math.max(1, Math.round(cssW * dpr));
			canvas.height = Math.max(1, Math.round(cssH * dpr));
			draw(phaseRef.current);
		};
		measure();
		const ro = new ResizeObserver(measure);
		ro.observe(canvas);
		return () => ro.disconnect();
	}, [draw, maxDpr]);

	// Animation: redraw imperatively each frame via rAF — no React re-render.
	useEffect(() => {
		if (!animate) {
			draw(0);
			return;
		}
		let raf = 0;
		const tick = () => {
			const phase = (performance.now() / 1000 / duration) % 1;
			phaseRef.current = phase;
			draw(phase);
			raf = requestAnimationFrame(tick);
		};
		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, [animate, duration, draw]);

	return (
		<canvas
			ref={canvasRef}
			role="img"
			aria-label={ariaLabel}
			className={className}
			style={{
				display: "block",
				...(responsive
					? { aspectRatio: `${vbWidth} / ${vbHeight}`, width: "100%" }
					: { height: vbHeight, width: vbWidth }),
				...style,
			}}
		/>
	);
}

const TAU = Math.PI * 2;
